import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Typography,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useGetMyComplianceStatus } from '../../hooks/queries/moodle/useGetMyComplianceStatus';

const WarningBanner = () => {
  const [open, setOpen] = useState(false);

  // We only enable the query if we haven't already dismissed the banner in this session
  const isDismissed =
    sessionStorage.getItem('warning_banner_dismissed') === 'true';

  const { courses, loading } = useGetMyComplianceStatus({
    enabled: !isDismissed,
  });

  const displayCourses = courses.filter(
    (c) =>
      c.completionState !== 'EXCLUDED' && c.completionState !== 'COMPLETED',
  );

  const todayStr = new Date().toDateString();
  const isDeadlineToday = (deadlineAt) => {
    if (!deadlineAt) return false;
    const deadlineDate = new Date(deadlineAt);
    return deadlineDate.toDateString() === todayStr;
  };

  const hasDeadlineToday = displayCourses.some((c) =>
    isDeadlineToday(c.deadlineAt),
  );

  useEffect(() => {
    if (!loading && !isDismissed && displayCourses.length > 0) {
      setOpen(true);
    }
  }, [loading, displayCourses.length, isDismissed]);

  const handleClose = () => {
    setOpen(false);
    sessionStorage.setItem('warning_banner_dismissed', 'true');
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: '100vw',
          height: '100vh',
          position: 'relative',
          overflow: 'visible',
        },
      }}
    >
      <Box
        sx={{
          backgroundImage: 'url(/assets/images/banner-image.png)',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundSize: '100% 100%',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          borderRadius: 1,
        }}
      />

      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: -24,
          top: -24,
          color: '#d32f2f',
          backgroundColor: '#ffffff',
          boxShadow: 3,
          transition: 'transform 0.2s',
          '&:hover': {
            backgroundColor: '#ffffff',
            color: '#b71c1c',
            transform: 'scale(1.2)',
          },
          zIndex: 20,
        }}
      >
        <CloseIcon sx={{ fontSize: 40, fontWeight: 'bold' }} />
      </IconButton>

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          px: 4,
          py: 6,
          textAlign: 'center',
        }}
      >
        <DialogTitle
          sx={{
            color: 'warning.main',
            p: 0,
            mt: 0,
            fontSize: '2rem',
            fontWeight: 'bold',
          }}
        >
          Course Compliance Reminder
        </DialogTitle>
        {hasDeadlineToday ? (
          <Typography
            variant="h5"
            mb={2}
            sx={{ fontWeight: 'bold', color: '#d32f2f' }}
          >
            Today is your deadline to complete your mandatory courses, otherwise
            it will block your beesheet!
          </Typography>
        ) : (
          <Typography variant="h5" mb={2} sx={{ fontWeight: 'bold' }}>
            You have pending mandatory courses. Please complete them before the
            deadline.
          </Typography>
        )}
        <List
          dense
          disablePadding
          sx={{
            mb: 3,
            overflowY: 'auto',
            maxHeight: '50vh',
            width: '100%',
            px: 2,
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#bdbdbd',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#9e9e9e',
            },
          }}
        >
          {displayCourses.map((course) => {
            const isToday = isDeadlineToday(course.deadlineAt);
            return (
              <ListItem
                key={course.moodleCourseId}
                disablePadding
                sx={{ justifyContent: 'center' }}
              >
                <ListItemText
                  sx={{ textAlign: 'center' }}
                  primary={
                    <a
                      href={course.courseUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        fontWeight: 'bold',
                        fontSize: '1rem',
                        color: isToday ? '#d32f2f' : '#1976d2',
                        textDecoration: 'underline',
                      }}
                    >
                      {course.courseName}
                    </a>
                  }
                  secondary={
                    <Box component="span" sx={{ fontSize: '0.875rem' }}>
                      Deadline:{' '}
                      {course.deadlineAt
                        ? new Date(course.deadlineAt).toLocaleDateString()
                        : '—'}
                    </Box>
                  }
                />
              </ListItem>
            );
          })}
        </List>
      </Box>
    </Dialog>
  );
};

export default WarningBanner;
